import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { USERS_ADMIN_ROLES } from "@/features/auth/permissions";
import { UserAvatarMenu } from "./user-avatar-menu";
import type { UserRole } from "@/types/api";

interface NavItem {
  to: string;
  label: string;
  /** If set, only users whose role is in this list see the link. */
  requiredRoles?: readonly UserRole[];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/schedules", label: "Schedules" },
  { to: "/users", label: "Users", requiredRoles: USERS_ADMIN_ROLES },
];

/**
 * Sticky 56 px header with nav pills + "Butler Tech" + avatar.
 * Matches prototype `Class Scheduler.dc.html:72-86`.
 */
export function MainLayout() {
  const { user } = useAuth();
  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      !item.requiredRoles || (user && item.requiredRoles.includes(user.role)),
  );

  return (
    <div className="min-h-screen bg-page">
      <header className="bg-surface border-b border-border-subtle sticky top-0 z-20">
        <div className="px-6 h-14 flex items-center gap-7">
          <nav className="flex gap-1" aria-label="Primary">
            {visibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-surface-2 text-white"
                      : "text-text-muted hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3.5">
            <span className="text-xs text-text-muted">Butler Tech</span>
            <UserAvatarMenu />
          </div>
        </div>
      </header>
      <main className="px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
